import React from 'react';
import StatsPlot from './StatsPlot';
import { formatInTimeZone } from '../utils/timezone';
import './TelemetryPanel.css';

const TelemetryPanel = ({ currentLocation, statsHistory, currentTime, timeZone }) => {
  if (!currentLocation) {
    return (
      <div className="telemetry-panel">
        <div className="telemetry-header">
          <h2>Telemetry</h2>
        </div>
        <div className="telemetry-empty">
          <p>No location data available</p>
        </div>
      </div>
    );
  }

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === void 0) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  const formatSpeed = (speed) => {
    if (speed === null || speed === void 0) return 'N/A';
    // Convert m/s to km/h
    const kmh = speed * 3.6;
    return `${formatNumber(kmh, 1)} km/h`;
  };

  const formatAltitude = (altitude) => {
    if (altitude === null || altitude === void 0) return 'N/A';
    return `${formatNumber(altitude, 1)} m`;
  };

  const getCardinalDirection = (course) => {
    if (course === null || course === void 0 || course < 0) return 'N/A';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(course / 45) % 8;
    return directions[index];
  };

  return (
    <div className="telemetry-panel">
      <div className="telemetry-header">
        <h2>Telemetry</h2>
      </div>
      
      <div className="telemetry-content">
        {statsHistory && statsHistory.length > 1 && currentTime && (
          <div className="telemetry-section stats-section">
            <h3>Statistics</h3>
            <StatsPlot
              data={statsHistory}
              currentTime={currentTime}
              dataKey="maxVelocitymps"
              label="Max Velocity"
              unit="km/h"
              color="#667eea"
            />
            <StatsPlot
              data={statsHistory}
              currentTime={currentTime}
              dataKey="totVerticalMeters"
              label="Total Vertical"
              unit="m"
              color="#764ba2"
            />
          </div>
        )}

        <div className="telemetry-section">
          <h3>Position</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Location</span>
              <span className="telemetry-value">
                {formatNumber(currentLocation.lat, 6)}°, {formatNumber(currentLocation.lon, 6)}°
              </span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Altitude</span>
              <span className="telemetry-value">{formatAltitude(currentLocation.altitude)}</span>
            </div>
          </div>
        </div>

        <div className="telemetry-section">
          <h3>Movement</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Speed</span>
              <span className="telemetry-value">{formatSpeed(currentLocation.speed)}</span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Course</span>
              <span className="telemetry-value">
                {formatNumber(currentLocation.course, 1)}° {getCardinalDirection(currentLocation.course)}
              </span>
            </div>
          </div>
        </div>

        <div className="telemetry-section">
          <h3>Accuracy</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Horizontal</span>
              <span className="telemetry-value">{formatNumber(currentLocation.accuracy, 2)} m</span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Vertical</span>
              <span className="telemetry-value">
                {currentLocation.verticalAccuracy !== void 0 
                  ? `${formatNumber(currentLocation.verticalAccuracy, 2)} m`
                  : 'N/A'}
              </span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Speed</span>
              <span className="telemetry-value">
                {currentLocation.speedAccuracy !== void 0 
                  ? `${formatNumber(currentLocation.speedAccuracy, 2)} m/s`
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="telemetry-section">
          <h3>Timestamp</h3>
          <div className="telemetry-item-full">
            <span className="telemetry-label">Time</span>
            <span className="telemetry-value">
              {timeZone
                ? formatInTimeZone(currentLocation.timestamp * 1000, timeZone, { dateStyle: 'medium', timeStyle: 'long' })
                : new Date(currentLocation.timestamp * 1000).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelemetryPanel;
